import React, { useState, useEffect } from 'react';
import { format, isToday, parseISO, startOfMonth, endOfMonth,
         eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths } from 'date-fns';
import axios from 'axios';
import { API_URL, getAuthHeader } from '../../utils/config';

export default function UserCalendarModal({ user, onClose, onSchedule }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const month = format(currentMonth, 'yyyy-MM-01');
        const { data: qtalkData } = await axios.get(`${API_URL}/meetings/user/${user.id}`, { headers: getAuthHeader() });
        let ext = [];
        try {
          const { data: cal } = await axios.get(`${API_URL}/calendar/my?user_id=${user.id}&month=${month}`, { headers: getAuthHeader() });
          ext = (cal.external || []).map(e => ({ ...e, scheduled_at: e.start_at, _type: 'external' }));
        } catch(e2) {}
        setMeetings([...(qtalkData||[]).map(m => ({ ...m, _type: 'meeting' })), ...ext]);
      } catch(e) { setMeetings([]); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [user.id, currentMonth]);

  const days = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  const startPad = startOfMonth(currentMonth).getDay();
  const getMeetingsForDay = (day) => meetings.filter(m => { try { return isSameDay(parseISO(m.scheduled_at || m.start_at), day); } catch(e) { return false; } });
  const selectedMeetings = getMeetingsForDay(selectedDay);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4"
          style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold overflow-hidden">
              {user.avatar_url?.startsWith('https://')
                ? <img src={user.avatar_url?.startsWith("https://") ? user.avatar_url : `https://qtalk.qualtechedge.in${user.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                : user.full_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-white font-bold text-sm">{user.full_name}'s Calendar</p>
              <p className="text-white/70 text-xs">Upcoming meetings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onSchedule}
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold rounded-xl transition flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Schedule
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-4 h-4">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="flex" style={{height:'420px'}}>
          {/* Left — mini calendar */}
          <div className="w-56 flex-shrink-0 border-r border-gray-100 dark:border-gray-700 p-3">
            {/* Month nav */}
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => setCurrentMonth(subMonths(currentMonth,1))}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span className="text-xs font-bold text-gray-800 dark:text-gray-100">{format(currentMonth,'MMM yyyy')}</span>
              <button onClick={() => setCurrentMonth(addMonths(currentMonth,1))}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
            <div className="grid grid-cols-7 mb-1">
              {['S','M','T','W','T','F','S'].map((d,i) => (
                <div key={i} className="text-center text-xs font-medium text-gray-400 py-0.5">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {Array(startPad).fill(null).map((_,i) => <div key={`p${i}`}/>)}
              {days.map(day => {
                const hasMeetings = getMeetingsForDay(day).length > 0;
                const isSelected = isSameDay(day, selectedDay);
                const todayDay = isToday(day);
                return (
                  <button key={day.toString()} onClick={() => setSelectedDay(day)}
                    className={`relative flex flex-col items-center py-1 rounded-lg transition text-xs font-medium
                      ${isSelected ? 'bg-teal-500 text-white' : todayDay ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                      ${!isSameMonth(day,currentMonth) ? 'opacity-30' : ''}`}>
                    {format(day,'d')}
                    {hasMeetings && <span className={`w-1 h-1 rounded-full mt-0.5 ${isSelected?'bg-white':'bg-teal-500'}`}/>}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs text-gray-400 mb-1.5 font-medium">
                {meetings.length} upcoming meeting{meetings.length !== 1 ? 's' : ''}
              </p>
              {meetings.slice(0,3).map(m => (
                <div key={m.id} className="flex items-center gap-1.5 mb-1">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${m._type==='external'?'bg-purple-400':'bg-teal-500'}`}/>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">🔒 Busy</span>
                </div>
              ))}
              {meetings.length > 3 && <p className="text-xs text-gray-400">+{meetings.length-3} more</p>}
            </div>
          </div>

          {/* Right — day detail */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-bold text-gray-800 dark:text-gray-100 text-sm">
                  {isToday(selectedDay) ? 'Today' : format(selectedDay,'EEEE')}
                </p>
                <p className="text-xs text-gray-400">{format(selectedDay,'MMMM d, yyyy')}</p>
              </div>
              {selectedMeetings.length > 0 && (
                <span className="text-xs px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded-full font-semibold">
                  {selectedMeetings.length} meeting{selectedMeetings.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-8 h-8 rounded-full border-4 border-teal-200 border-t-teal-500 animate-spin"/>
              </div>
            ) : selectedMeetings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-10 h-10 mb-2 opacity-20">
                  <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <p className="text-sm font-medium">No meetings on this day</p>
                <button onClick={onSchedule} className="mt-2 text-xs text-teal-500 hover:text-teal-600 font-semibold transition">
                  + Schedule a meeting
                </button>
              </div>
            ) : (
              selectedMeetings.map(m => {
                const dt = m.scheduled_at || m.start_at;
                let meetDate;
                try { meetDate = parseISO(dt); } catch(e) { meetDate = new Date(dt); }
                return (
                  <div key={m.id} className="rounded-xl p-3 mb-2 border bg-gray-50 dark:bg-gray-800 border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-base">🔒</span>
                      <p className="font-semibold text-sm text-gray-700 dark:text-gray-200">Busy</p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                      <span>🕐 {format(meetDate,'HH:mm')}</span>
                      {m.duration_minutes && <span>⏱ {m.duration_minutes} min</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
